import { useGetBeheerVossen, useGetBeheerVosStations, useSyncVosMetStations } from "@/entities/medewerkers-op-station";
import { VossenTable, VosStationsTable } from "@/features/medewerkers-op-station";
import { Button, Container, DataTableSkeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components";
import { ErrorBoundary, ErrorDisplay } from "@/shared/components/error";
import { useSearchParams } from "react-router";

function VossenContent() {
    const { data: beheerVossen, isPending, error } = useGetBeheerVossen();

    if (isPending) {
        return <Loading title="Vrijwilligers op stations" description="Beheer vrijwilligers op station" />;
    }

    if (error) {
        return <ErrorDisplay error={error} variant="section" title="Fout bij het laden van vossen" />;
    }

    return (
        <div className="py-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-xxl text-content-default font-bold">Vrijwilligers op stations</h1>
                    <p className="text-s text-content-subtle mt-2">Beheer vrijwilligers op station</p>
                </div>
            </div>
            <VossenTable vossen={beheerVossen} />
        </div>
    );
}

function StationsContent() {
    const { data: vosStations, isPending, error } = useGetBeheerVosStations();
    const syncVosMetStations = useSyncVosMetStations();

    const handleSync = () => {
        syncVosMetStations.mutate();
    };

    if (isPending) {
        return <Loading title="Stationsgegevens" description="Beheer vrijwilligers op station" />;
    }

    if (error) {
        return <ErrorDisplay error={error} variant="section" title="Fout bij het laden van vos stations" />;
    }

    return (
        <div className="py-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-xxl text-content-default font-bold">Stationsgegevens</h1>
                    <p className="text-s text-content-subtle mt-2">Beheer vrijwilligers op station</p>
                </div>
                <Button
                    variant="secondary"
                    onClick={handleSync}
                    disabled={syncVosMetStations.isPending}
                    icon="refresh"
                    data-testid="sync-vos-stations"
                >
                    {syncVosMetStations.isPending ? "Synchroniseren..." : "Sync met Stationsdata"}
                </Button>
            </div>
            <VosStationsTable stations={vosStations} />
        </div>
    );
}

type LoadingProps = {
    title: string;
    description: string;
};

/**
 * Loading fallback component for when vossen are being fetched
 */
function Loading({ title, description }: LoadingProps) {
    return (
        <div className="py-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-xxl text-content-default font-bold">{title}</h1>
                    <p className="text-s text-content-subtle mt-2">{description}</p>
                </div>
            </div>
            <DataTableSkeleton rowCount={10} columnCount={8} />
        </div>
    );
}

export default function BeheerVosEnStations() {
    // ensure we keep the selected tab open on refresh
    const [queryParams, setQueryParams] = useSearchParams(new URLSearchParams({ selectedTab: "vossen" }));
    const selectedTab = queryParams.get("selectedTab") ?? "vossen";
    const setSelectedTab = (selectedTab: string) => {
        setQueryParams(new URLSearchParams({ selectedTab }));
    };

    return (
        <Container fluid>
            <ErrorBoundary fallback={(error) => <ErrorDisplay error={error} variant="section" title="Fout bij het laden van vossen" />}>
                <Tabs value={selectedTab} className="gap-2" onValueChange={setSelectedTab}>
                    <TabsList>
                        <TabsTrigger value="vossen" className="relative">
                            Vrijwilligers
                        </TabsTrigger>
                        <TabsTrigger value="stations">Stations</TabsTrigger>
                    </TabsList>
                    <TabsContent value="vossen">
                        <VossenContent />
                    </TabsContent>
                    <TabsContent value="stations">
                        <StationsContent />
                    </TabsContent>
                </Tabs>
            </ErrorBoundary>
        </Container>
    );
}
